import {
  COMPONENT_TYPE_ADULT_GUEST,
  COMPONENT_TYPE_CHILD_GUEST,
  GUEST_TYPES,
  GuestInfo,
  Guests,
} from '@entities/leadCollector';
import { PlusIcon } from '@shared/assets/icons';
import { useEffect, useState, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

interface FormAddGuestProps {
  maxExtraAdults: number;
  maxExtraChildren: number;
  value: Guests;
  onChange: (guests: Guests) => void;
  isCanEdit: boolean;
}

const MAX_BUTTON_TEXT_LENGTH = 17;

export const FormAddGuest = ({ maxExtraAdults, maxExtraChildren, value, onChange, isCanEdit }: FormAddGuestProps) => {
  const [copyValue, setCopyValue] = useState<Guests>(value);
  const extraAdults = value.adultGuest?.length || 0;
  const extraChildren = value.childrenGuest?.length || 0;
  const isShowAdultButton = !!maxExtraAdults && (extraAdults !== maxExtraAdults || !isCanEdit);
  const isShowChildrenButton = !!maxExtraChildren && (extraChildren !== maxExtraChildren || !isCanEdit);
  const isNotGuests = !isShowAdultButton && !isShowChildrenButton;

  const defaultAdultButtonTitle = 'Взрослый';
  const defaultChildButtonTitle = 'Маленький';

  const adultButtonTitle = value.adultButtonTitle || defaultAdultButtonTitle;
  const childButtonTitle = value.childButtonTitle || defaultChildButtonTitle;

  const adultButtonTextRef = useRef<HTMLSpanElement>(null);
  const childButtonTextRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setCopyValue(value);
  }, [isCanEdit]);

  useEffect(() => {
    if (adultButtonTextRef.current) {
      adultButtonTextRef.current.textContent = `${adultButtonTitle}`;
    }
  }, []);

  useEffect(() => {
    if (childButtonTextRef.current) {
      childButtonTextRef.current.textContent = `${childButtonTitle}`;
    }
  }, []);

  if (isNotGuests) {
    return null;
  }

  const onAddGuest = (guestType: GUEST_TYPES) => {
    switch (guestType) {
      case COMPONENT_TYPE_ADULT_GUEST: {
        if (!copyValue.adultGuest?.[0]) return;
        value.adultGuest?.push(copyGuest(copyValue.adultGuest[0]));
        break;
      }
      case COMPONENT_TYPE_CHILD_GUEST:
        if (!copyValue.childrenGuest?.[0]) return;
        value.childrenGuest?.push(copyGuest(copyValue.childrenGuest[0]));
        break;
    }

    onChange(value);
  };

  const copyGuest = (guest: GuestInfo) => {
    const newGuest = structuredClone(guest);

    newGuest.FIO = newGuest?.FIO.map((v) => ({ ...v, instanceId: `${Date.now()}-${Math.random()}` }));
    newGuest.additionalFields = newGuest?.additionalFields.map((v) => ({
      ...v,
      instanceId: `${Date.now()}-${Math.random()}`,
    }));

    return newGuest;
  };

  const handleAdultButtonBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    if (adultButtonTextRef.current && !e.currentTarget.textContent?.trim()) {
      adultButtonTextRef.current.textContent = defaultAdultButtonTitle;
    }

    onChange({ ...value, adultButtonTitle: e.currentTarget.textContent?.trim() || undefined });
  };

  const handleChildButtonBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    if (childButtonTextRef.current && !e.currentTarget.textContent?.trim()) {
      childButtonTextRef.current.textContent = defaultChildButtonTitle;
    }

    onChange({ ...value, childButtonTitle: e.currentTarget.textContent?.trim() || undefined });
  };

  const handleAdultButtonFocus = () => {
    if (adultButtonTextRef.current && adultButtonTextRef.current.textContent === defaultAdultButtonTitle) {
      adultButtonTextRef.current.textContent = '';
      adultButtonTextRef.current.focus();
    }
  };

  const handleChildButtonFocus = () => {
    if (childButtonTextRef.current && childButtonTextRef.current.textContent === defaultChildButtonTitle) {
      childButtonTextRef.current.textContent = '';
      childButtonTextRef.current.focus();
    }
  };
  const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    const text = e.currentTarget.textContent || '';
    if (text.length >= MAX_BUTTON_TEXT_LENGTH && e.key !== 'Backspace' && e.key !== 'Delete') {
      e.preventDefault();
    }
  };

  return (
    <div {...stylex.props(styles.container, isCanEdit && styles.containerEditable)}>
      <span {...stylex.props(styles.title)}>Добавить гостей</span>

      <div {...stylex.props(styles.buttons)}>
        {isShowAdultButton && (
          <button
            {...stylex.props(styles.button)}
            disabled={isCanEdit && extraAdults === maxExtraAdults}
            onClick={(e) => {
              if (isCanEdit && extraAdults !== maxExtraAdults) {
                if (!isCanEdit && adultButtonTextRef.current?.contains(e.target as Node)) {
                  return;
                }
                onAddGuest(COMPONENT_TYPE_ADULT_GUEST);
              }
            }}
          >
            <PlusIcon height="24" width="24" />
            <span
              {...stylex.props(
                styles.editableButtonText,
                isCanEdit ? styles.editableButtonTextPreview : styles.editableButtonTextEdit
              )}
              ref={adultButtonTextRef}
              suppressContentEditableWarning
              contentEditable={!isCanEdit}
              onBlur={handleAdultButtonBlur}
              onMouseUp={isCanEdit ? undefined : handleAdultButtonFocus}
              onKeyDown={handleButtonKeyDown}
            >
              {adultButtonTextRef.current?.textContent || adultButtonTitle}
            </span>
          </button>
        )}

        {isShowChildrenButton && (
          <button
            {...stylex.props(styles.button)}
            disabled={isCanEdit && extraChildren === maxExtraChildren}
            onClick={(e) => {
              if (isCanEdit && extraChildren !== maxExtraChildren) {
                if (!isCanEdit && childButtonTextRef.current?.contains(e.target as Node)) {
                  return;
                }
                onAddGuest(COMPONENT_TYPE_CHILD_GUEST);
              }
            }}
          >
            <PlusIcon height="24" width="24" />
            <span
              {...stylex.props(
                styles.editableButtonText,
                isCanEdit ? styles.editableButtonTextPreview : styles.editableButtonTextEdit
              )}
              ref={childButtonTextRef}
              suppressContentEditableWarning
              contentEditable={!isCanEdit}
              onBlur={handleChildButtonBlur}
              onMouseUp={isCanEdit ? undefined : handleChildButtonFocus}
              onKeyDown={handleButtonKeyDown}
            >
              {childButtonTextRef.current?.textContent || childButtonTitle}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
